/**
 * File: useSystemReportState.ts
 * Path: apps/admin/src/features/diagnostics/hooks/useSystemReportState.ts
 * Description: Runs the server's checks (runDiagnostics), admins only.
 */

import { useCallback, useEffect, useState } from "react";
import { FunctionsService, type SystemReport } from "@idk/config";

interface State {
  report: SystemReport | null;
  loading: boolean;
  error: string | null;
}

export const useSystemReport = (enabled: boolean) => {
  const [state, setState] = useState<State>({ report: null, loading: enabled, error: null });

  const run = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const report = await FunctionsService.call<undefined, SystemReport>("runDiagnostics");
      setState({ report, loading: false, error: null });
    } catch (error) {
      setState({
        report: null,
        loading: false,
        error: error instanceof Error ? error.message : "The server didn't answer.",
      });
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    // Deferred so the first state change isn't inside the effect itself.
    const timer = window.setTimeout(() => void run(), 0);
    return () => window.clearTimeout(timer);
  }, [enabled, run]);

  return { ...state, run };
};
